// Station × config comparison matrix for «Сходимость» — the bird's-eye
// answer to "for each station, how fast / how accurate is each config?".
//
// Three column modes (operator segment-control in the header), all reading the
// SAME per-cell `curve` (median |dR| per length) the heatmap endpoint already
// ships — no extra data:
//   * «время»       — column per config; cell = hours-to-threshold (reactive
//                     to the tab threshold). Colour: hoursColor (green→red).
//   * «|dR| 24ч»     — column per config; cell = |dR| (mm) at the longest
//                     interval. Colour: drColor (same scale as Точность-24h).
//   * «сравнение»    — custom columns = (config × interval length) pairs, up
//                     to 5; cell = median |dR| (mm) of the station at that
//                     pair. Colour: drColor. (The time / dr24 modes are just
//                     presets of this same table.)
//
// scale="mono" swaps both gradients for a colour-blind-safe parchment→ink ramp.
// Tap a station name → open its series in the list drill (onStationTap).
//
// Glyphs:  number  hours / mm    —  never reaches (time only)
//          ○  no reference       ✗  series FAILED
//          ·  not at this length (compare)   (empty)  not tested

import { memo, useMemo, useState } from 'react';
import type { ReactElement } from 'react';

import type {
  ConvergenceHeatmapCell,
  ConvergenceHeatmapData,
} from '../../api/types';
import {
  drColor,
  drTextColor,
  formatConfig,
  formatLen,
  hoursColor,
  hoursTextColor,
  median,
  monoColor,
  monoText,
  timeToThreshold,
} from './convergenceScales';

type Mode = 'time' | 'dr24' | 'compare';
type Scale = 'color' | 'mono';
type SortMode = 'accuracy' | 'coverage' | 'name';

type ColKind = 'time' | 'dr24' | 'drlen';
interface Column {
  key: string;
  config: string;
  kind: ColKind;
  length?: number;
  label: string;
  sub?: string;
}

interface Resolved {
  state: 'num' | 'never' | 'noref' | 'fail' | 'gap' | 'empty';
  val?: number;
}

const MAX_COMPARE_COLS = 5;

function ConvergenceHeatmapImpl({
  data,
  threshold,
  onStationTap,
}: {
  data: ConvergenceHeatmapData;
  threshold: number;
  onStationTap?: (station: string) => void;
}) {
  const [mode, setMode] = useState<Mode>('time');
  const [scale, setScale] = useState<Scale>('color');
  const [sort, setSort] = useState<SortMode>('accuracy');
  const [customCols, setCustomCols] = useState<{ config: string; length: number }[]>([]);
  const [pickConfig, setPickConfig] = useState('');
  const [pickLength, setPickLength] = useState('');

  const { stations, configs, cells, lengths } = data;
  const isDr = mode !== 'time'; // dr24 + compare are |dR| (mm); time is hours
  const mono = scale === 'mono';
  const monoMin = isDr ? 0 : 1.5;
  const monoMax = isDr ? 60 : 24;

  // ── columns by mode (memoized) ──
  const columns: Column[] = useMemo(
    () =>
      mode === 'time'
        ? configs.map((c) => ({ key: c, config: c, kind: 'time', label: formatConfig(c) }))
        : mode === 'dr24'
          ? configs.map((c) => ({ key: c, config: c, kind: 'dr24', label: formatConfig(c) }))
          : customCols.map((cc) => ({
              key: `${cc.config}@${cc.length}`,
              config: cc.config,
              kind: 'drlen' as const,
              length: cc.length,
              label: formatConfig(cc.config),
              sub: formatLen(cc.length),
            })),
    [mode, configs, customCols],
  );

  // C1.1 — build a Map<"station|config" → cell> ONCE per data change instead of
  // cells.find() per cell. Was O(S²·C²) across resolve / partition / sort /
  // render; first-match kept (only set the first cell for a key) so behaviour
  // is byte-identical. See test_convergence_heatmap lookup/sort/partition tests.
  const cellMap = useMemo(() => {
    const m = new Map<string, ConvergenceHeatmapCell>();
    for (const c of cells) {
      const k = `${c.station}|${c.config}`;
      if (!m.has(k)) m.set(k, c);
    }
    return m;
  }, [cells]);

  const cellBg = (v: number): string =>
    mono ? monoColor(v, monoMin, monoMax) : isDr ? drColor(v) : hoursColor(v);
  const cellFg = (v: number): string =>
    mono ? monoText(v, monoMin, monoMax) : isDr ? drTextColor(v) : hoursTextColor(v);

  // Resolve every (station × column) ONCE, and derive per-station coverage +
  // median from the resolved rows (no per-cell re-find, no per-comparison
  // recompute). renderRow reads these; the sort/partition read the stats.
  const { resolvedRows, filledCount, rowMed } = useMemo(() => {
    const resolveCell = (st: string, col: Column): Resolved => {
      const cell = cellMap.get(`${st}|${col.config}`);
      if (!cell) return { state: 'empty' };
      if (cell.state !== 'curve' || !cell.curve) return { state: cell.state as Resolved['state'] };
      if (col.kind === 'time') {
        const hrs = timeToThreshold(cell.curve, threshold, lengths);
        return hrs == null ? { state: 'never' } : { state: 'num', val: hrs };
      }
      if (col.kind === 'dr24') {
        let v: number | null = null;
        for (let i = cell.curve.length - 1; i >= 0; i--) {
          if (cell.curve[i] != null) {
            v = cell.curve[i]!;
            break;
          }
        }
        return v == null ? { state: 'noref' } : { state: 'num', val: v };
      }
      const idx = lengths.indexOf(col.length!);
      const v = idx >= 0 ? cell.curve[idx] : null;
      return v == null ? { state: 'gap' } : { state: 'num', val: v };
    };
    const resolvedRows = new Map<string, Resolved[]>();
    const filledCount = new Map<string, number>();
    const rowMed = new Map<string, number | null>();
    for (const st of stations) {
      const rs = columns.map((col) => resolveCell(st, col));
      resolvedRows.set(st, rs);
      filledCount.set(
        st,
        rs.filter((r) => r.state === 'num' || r.state === 'never' || r.state === 'noref').length,
      );
      rowMed.set(st, median(rs.filter((r) => r.state === 'num').map((r) => r.val!)));
    }
    return { resolvedRows, filledCount, rowMed };
  }, [cellMap, columns, threshold, lengths, stations]);

  const { sorted, empty } = useMemo(() => {
    const filledSt = stations.filter((s) => (filledCount.get(s) ?? 0) > 0);
    const emptySt = stations.filter((s) => (filledCount.get(s) ?? 0) === 0);
    const sortedSt = [...filledSt].sort((a, b) => {
      if (sort === 'name') return a.localeCompare(b);
      if (sort === 'coverage')
        return (filledCount.get(b) ?? 0) - (filledCount.get(a) ?? 0) || a.localeCompare(b);
      const ma = rowMed.get(a) ?? null;
      const mb = rowMed.get(b) ?? null;
      if (ma == null && mb == null) return a.localeCompare(b);
      if (ma == null) return 1;
      if (mb == null) return -1;
      return ma - mb || a.localeCompare(b);
    });
    return { sorted: sortedSt, empty: emptySt };
  }, [stations, filledCount, rowMed, sort]);

  const caption =
    mode === 'time'
      ? `Время до точности ${threshold} мм — часы наблюдений, за которые медианная кривая станции опускается до ${threshold} мм. Зелёный = быстрее, «—» = не достигает.`
      : mode === 'dr24'
        ? 'Точность на 24 ч — медианный |dR| (мм) станции на самом длинном интервале. Зелёный = точнее.'
        : 'Сравнение |dR| (мм) по выбранным «конфиг × длина» — медиана станции. Зелёный = точнее.';

  const addColumn = () => {
    const length = parseFloat(pickLength);
    if (!pickConfig || !Number.isFinite(length)) return;
    if (customCols.length >= MAX_COMPARE_COLS) return;
    if (customCols.some((c) => c.config === pickConfig && c.length === length)) return;
    setCustomCols((cols) => [...cols, { config: pickConfig, length }]);
  };
  const removeColumn = (key: string) =>
    setCustomCols((cols) => cols.filter((c) => `${c.config}@${c.length}` !== key));

  const renderRow = (st: string) => (
    <tr key={st}>
      <th
        className={`heatmap-row-header${onStationTap ? ' conv-row-tap' : ''}`}
        onClick={onStationTap ? () => onStationTap(st) : undefined}
        title={onStationTap ? `${st}: открыть серию` : st}
        data-testid={`conv-matrix-row-${st}`}
      >
        {st}
      </th>
      {columns.map((col, ci) => {
        const r = (resolvedRows.get(st) ?? [])[ci] ?? { state: 'empty' as const };
        let bg = 'var(--surface)';
        let fg = 'var(--ink-3)';
        let glyph = '';
        let title = `${st} × ${col.label}${col.sub ? ` ${col.sub}` : ''}: не тестировалось`;
        if (r.state === 'num') {
          bg = cellBg(r.val!);
          fg = cellFg(r.val!);
          glyph = r.val!.toFixed(1);
          title =
            col.kind === 'time'
              ? `${st} × ${col.label}: ${r.val!.toFixed(1)} ч до ${threshold} мм`
              : `${st} × ${col.label}${col.sub ? ` ${col.sub}` : ' 24ч'}: |dR| ${r.val!.toFixed(1)} мм`;
        } else if (r.state === 'never') {
          bg = 'var(--surface-2)';
          glyph = '—';
          title = `${st} × ${col.label}: не достигает ${threshold} мм за ${formatLen(lengths[lengths.length - 1]!)}`;
        } else if (r.state === 'noref') {
          bg = 'var(--surface-2)';
          glyph = '○';
          title = `${st} × ${col.label}: без эталона`;
        } else if (r.state === 'fail') {
          bg = 'var(--surface-2)';
          glyph = '✗';
          title = `${st} × ${col.label}: серия FAILED`;
        } else if (r.state === 'gap') {
          glyph = '·';
          title = `${st} × ${col.label} ${col.sub ?? ''}: этой длины нет`;
        }
        return (
          <td
            key={col.key}
            className="heatmap-cell"
            style={{ backgroundColor: bg, color: fg, fontWeight: r.state === 'num' ? 600 : 500 }}
            title={title}
          >
            {glyph}
          </td>
        );
      })}
    </tr>
  );

  return (
    <div className="heatmap-section">
      {/* ── header: mode + scale segment controls ── */}
      <div className="conv-matrix-head">
        <div className="conv-seg-row">
          <div className="conv-seg" role="group" aria-label="режим матрицы">
            <button type="button" className={mode === 'time' ? 'on' : ''} onClick={() => setMode('time')} data-testid="conv-matrix-metric-time">
              время
            </button>
            <button type="button" className={mode === 'dr24' ? 'on' : ''} onClick={() => setMode('dr24')} data-testid="conv-matrix-metric-dr24">
              |dR| 24ч
            </button>
            <button type="button" className={mode === 'compare' ? 'on' : ''} onClick={() => setMode('compare')} data-testid="conv-matrix-mode-compare">
              сравнение
            </button>
          </div>
          <div className="conv-seg" role="group" aria-label="шкала матрицы">
            <button type="button" className={!mono ? 'on' : ''} onClick={() => setScale('color')} data-testid="conv-matrix-scale-color">
              цвет
            </button>
            <button type="button" className={mono ? 'on' : ''} onClick={() => setScale('mono')} data-testid="conv-matrix-scale-mono">
              моно
            </button>
          </div>
        </div>
        <div className="conv-matrix-caption">{caption}</div>
      </div>

      {/* ── compare-mode column builder ── */}
      {mode === 'compare' && (
        <div className="conv-colbuilder">
          <div className="conv-colbuilder-row">
            <select
              className="filter-select gnss-qa-select"
              value={pickConfig}
              onChange={(e) => setPickConfig(e.target.value)}
              aria-label="конфиг колонки"
            >
              <option value="">конфиг…</option>
              {configs.map((c) => (
                <option key={c} value={c}>{formatConfig(c)}</option>
              ))}
            </select>
            <select
              className="filter-select gnss-qa-select"
              value={pickLength}
              onChange={(e) => setPickLength(e.target.value)}
              aria-label="длина интервала колонки"
            >
              <option value="">длина…</option>
              {lengths.map((l) => (
                <option key={l} value={l}>{formatLen(l)}</option>
              ))}
            </select>
            <button
              type="button"
              className="conv-coladd"
              onClick={addColumn}
              disabled={!pickConfig || !pickLength || customCols.length >= MAX_COMPARE_COLS}
              data-testid="conv-matrix-add-col"
            >
              + колонка
            </button>
            <span className="conv-colcount">{customCols.length}/{MAX_COMPARE_COLS}</span>
          </div>
          {customCols.length > 0 ? (
            <div className="conv-colchips">
              {customCols.map((cc) => {
                const key = `${cc.config}@${cc.length}`;
                return (
                  <span key={key} className={`at-chip cfg-${cc.config} on`}>
                    <span className="dot" />
                    {formatConfig(cc.config)} · {formatLen(cc.length)}
                    <button type="button" className="conv-colchip-x" onClick={() => removeColumn(key)} aria-label="убрать колонку">
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          ) : (
            <div className="conv-colbuilder-hint">
              добавьте до {MAX_COMPARE_COLS} колонок «конфиг × длина» (напр. GPS·3ч, ГЛО·3ч, все 4·1.5ч), чтобы сравнить точность станций при разном времени наблюдений
            </div>
          )}
        </div>
      )}

      {columns.length === 0 ? (
        <div className="at-empty">выберите колонки для сравнения ↑</div>
      ) : (
        <>
          <HeatmapLegend mode={mode} />

          <div className="heatmap-sort-row">
            <label className="heatmap-sort-label" htmlFor="conv-heatmap-sort">сортировка</label>
            <select
              id="conv-heatmap-sort"
              className="filter-select gnss-qa-select"
              value={sort}
              onChange={(e) => setSort(e.target.value as SortMode)}
            >
              <option value="accuracy">{mode === 'time' ? 'по скорости сходимости' : 'по точности'}</option>
              <option value="coverage">по покрытию колонок</option>
              <option value="name">по имени</option>
            </select>
          </div>

          <div className="heatmap-wrap">
            <table className="heatmap-table">
              <thead>
                <tr>
                  <th className="heatmap-corner">станция</th>
                  {columns.map((col) => (
                    <th key={col.key} className="heatmap-col-header">
                      {col.label}
                      {col.sub && <span className="conv-col-sub">{col.sub}</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>{sorted.map(renderRow)}</tbody>
            </table>
          </div>

          <div className="heatmap-legend">
            {mono ? (
              <>
                <span className="legend-swatch" style={{ background: monoColor(monoMin, monoMin, monoMax) }}>{isDr ? '0 мм' : 'быстро'}</span>
                <span className="legend-swatch" style={{ background: monoColor((monoMin + monoMax) / 2, monoMin, monoMax) }}>средне</span>
                <span className="legend-swatch" style={{ background: monoColor(monoMax, monoMin, monoMax), color: '#fff' }}>{isDr ? '60+ мм' : '24+ ч'}</span>
              </>
            ) : isDr ? (
              <>
                <span className="legend-swatch" style={{ background: drColor(5), color: '#fff' }}>&lt; 15</span>
                <span className="legend-swatch" style={{ background: drColor(25) }}>15–50</span>
                <span className="legend-swatch" style={{ background: drColor(75), color: '#fff' }}>&gt; 50 мм</span>
              </>
            ) : (
              <>
                <span className="legend-swatch" style={{ background: hoursColor(2.5), color: '#fff' }}>&lt; 3 ч</span>
                <span className="legend-swatch" style={{ background: hoursColor(8) }}>3–12 ч</span>
                <span className="legend-swatch" style={{ background: hoursColor(20), color: '#fff' }}>&gt; 16 ч</span>
              </>
            )}
          </div>

          {empty.length > 0 && (
            <CollapsibleEmpty stations={empty} columns={columns} renderRow={renderRow} />
          )}
        </>
      )}
    </div>
  );
}

function HeatmapLegend({ mode }: { mode: Mode }) {
  const isTime = mode === 'time';
  return (
    <div className="heatmap-dash-legend">
      <span className="heatmap-dash-legend-item">
        <span className="heatmap-dash-legend-glyph">{isTime ? 'часы' : 'мм'}</span>
        <span>{isTime ? 'время до порога' : '|dR|'}</span>
      </span>
      <span className="heatmap-dash-legend-sep">·</span>
      {isTime && (
        <>
          <span className="heatmap-dash-legend-item">
            <span className="heatmap-dash-legend-glyph">—</span>
            <span>не достигает порога</span>
          </span>
          <span className="heatmap-dash-legend-sep">·</span>
        </>
      )}
      <span className="heatmap-dash-legend-item">
        <span className="heatmap-dash-legend-glyph">○</span>
        <span>без эталона</span>
      </span>
      <span className="heatmap-dash-legend-sep">·</span>
      <span className="heatmap-dash-legend-item">
        <span className="heatmap-dash-legend-glyph">✗</span>
        <span>FAILED</span>
      </span>
      <span className="heatmap-dash-legend-sep">·</span>
      <span className="heatmap-dash-legend-item">
        <span className="heatmap-dash-legend-glyph">⤵</span>
        <span>тап по станции — её серия</span>
      </span>
    </div>
  );
}

function CollapsibleEmpty({
  stations,
  columns,
  renderRow,
}: {
  stations: string[];
  columns: Column[];
  renderRow: (st: string) => ReactElement;
}) {
  const [open, setOpen] = useState(false);
  return (
    <section className="empty-stations-section">
      <button
        type="button"
        className="empty-stations-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="empty-stations-caret" aria-hidden="true">{open ? '▴' : '▾'}</span>{' '}
        СТАНЦИИ БЕЗ РЕЗУЛЬТАТОВ ({stations.length})
      </button>
      {open && (
        <div className="heatmap-wrap">
          <table className="heatmap-table">
            <thead>
              <tr>
                <th className="heatmap-corner">станция</th>
                {columns.map((col) => (
                  <th key={col.key} className="heatmap-col-header">
                    {col.label}
                    {col.sub && <span className="conv-col-sub">{col.sub}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>{[...stations].sort((a, b) => a.localeCompare(b)).map(renderRow)}</tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// C1.2 — memoized so a parent re-render (5s status poll / 30s tick / a light
// sibling query settling) does NOT re-run this heavy matrix when data/threshold/
// onStationTap are unchanged. Standard shallow compare (props are stabilized:
// data via React Query structuralSharing, onStationTap via useCallback).
export const ConvergenceHeatmap = memo(ConvergenceHeatmapImpl);
